import { SkeletonText } from '@dev-spendesk/grapes';
import React from 'react';

import { type I18nKey } from 'common/hooks/useTranslation';
import { useAccountingIntegrationStatusQuery } from 'modules/accounting-integration/apis';
import { useExpenseAccountQuery } from 'modules/bookkeep/prepare-payables/components/PreparePayablesInbox/hooks/useExpenseAccountQuery';
import { useExpenseAccountsQuery } from 'modules/bookkeep/prepare-payables/components/PreparePayablesInbox/hooks/useExpenseAccountsQuery';
import { type ItemLineExpenseAccount } from 'modules/bookkeep/prepare-payables/models';
import {
  accountIdFromAutomation,
  type ExpenseAccountAutomation,
  type ExpenseAmountAutomation,
} from 'modules/payable/models';

import { PayableExpenseAccountField } from './PayableExpenseAccountField';

type Props = {
  values: ItemLineExpenseAccount;
  initialValues: ItemLineExpenseAccount | null;
  errors?: {
    accountId?: string;
    amount?: string;
  };
  hasAmountError: boolean;
  expenseAccountId?: string | null; // to check for archived expense accounts
  accountAutomation?: ExpenseAccountAutomation;
  amountAutomation?: ExpenseAmountAutomation;
  shouldDisplayAmountValue: boolean;
  currency: string;
  expenseAccountPlaceholder?: I18nKey;
  onChange: (value: ItemLineExpenseAccount) => void;
  className?: string;
  hideWarningCallout?: boolean;
};

export const PayableExpenseAccountFieldContainer = ({
  values,
  initialValues,
  errors,
  hasAmountError,
  expenseAccountId,
  currency,
  accountAutomation,
  amountAutomation,
  shouldDisplayAmountValue,
  expenseAccountPlaceholder,
  onChange,
  className,
  hideWarningCallout,
}: Props) => {
  const expenseAccountsQuery = useExpenseAccountsQuery({
    includeArchived: true,
    isAvailable: true,
  });

  const accountId =
    values.accountId ?? accountIdFromAutomation(accountAutomation);

  const isExpenseAccountQueryEnabled = Boolean(accountId);

  const expenseAccountQuery = useExpenseAccountQuery(
    accountId,
    isExpenseAccountQueryEnabled,
  );

  const integrationStatusQuery = useAccountingIntegrationStatusQuery();

  if (
    expenseAccountsQuery.status !== 'success' ||
    (isExpenseAccountQueryEnabled &&
      expenseAccountQuery.status !== 'success') ||
    integrationStatusQuery.status !== 'success'
  ) {
    return <SkeletonText className="mb-4 !h-[30px]" />;
  }

  const expenseAccount =
    isExpenseAccountQueryEnabled && expenseAccountQuery.status === 'success'
      ? expenseAccountQuery.data
      : undefined;

  const archivedExpenseAccount = !expenseAccount
    ? expenseAccountsQuery.data.find(
        (account) => account.id === expenseAccountId,
      )
    : undefined;

  return (
    <PayableExpenseAccountField
      values={values}
      initialValues={initialValues}
      errors={errors}
      hasAmountError={hasAmountError}
      expenseAccounts={expenseAccountsQuery.data.filter(
        (expAccount) => !expAccount.isArchived,
      )}
      expenseAccount={expenseAccount}
      archivedExpenseAccount={archivedExpenseAccount}
      currency={currency}
      accountAutomation={accountAutomation}
      amountAutomation={amountAutomation}
      shouldDisplayAmountValue={shouldDisplayAmountValue}
      expenseAccountPlaceholder={expenseAccountPlaceholder}
      integrationStatus={integrationStatusQuery.data}
      onChange={onChange}
      className={className}
      hideWarningCallout={hideWarningCallout}
    />
  );
};
